import TaskStatus from '@/enums/taskStatus'
import TaskType from '@/enums/taskType'
import { QAR_READ_ONLY_LINK } from '@/features/qar/verification/common'
import { Task } from '@/models/workflow/task'
import useStore from '@/state/store'
import OorWorkflowTask from '@/types/oor-workflow-task'
import { TaskMenuItem } from '@/types/task-menu-item'
import { isQAR } from '@/utils/auth'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useOorWorkflowStore } from '../store'
import { oorTaskPageMapping } from '../task-page-mapping'
import { SideMenuItem } from './side-menu-item'

export const SideMenu = () => {
  const pathname = usePathname()
  const { workflow } = useOorWorkflowStore()
  const { authentication } = useStore()
  const [taskMenuItems, setTaskMenuItems] = useState<TaskMenuItem[]>([])

  // `userInfo` is nullable in the store until the auth state machine reaches
  // SignedIn. The side menu is only rendered inside the workflow shell, which
  // already gates on auth; treat the unsigned-in case as a no-op below.
  const userInfo = authentication.userInfo

  const getHref = (menuItem: TaskMenuItem, requesterId: string) => {
    if (menuItem.taskType === OorWorkflowTask.SCHEDULE_ISSUANCE_CEREMONY) {
      return requesterId === userInfo?.id
        ? '/oor/schedule'
        : '/oor/schedule/status'
    }
    return menuItem.href
  }

  const getDisabledOrNot = (taskType: OorWorkflowTask) => {
    let isDisabled = false
    if (taskType == OorWorkflowTask.PROPOSE_RECIPIENTS) {
      const currentTask = workflow?.findTaskByType(
        OorWorkflowTask.PROPOSE_RECIPIENTS
      )
      const prevTask = workflow?.findTaskByType(OorWorkflowTask.INTRODUCTION)
      if (!prevTask?.isTaskCompleted() && !currentTask?.isTaskCompleted()) {
        isDisabled = true
      }
    } else if (taskType == OorWorkflowTask.QAR_ORG_VERIFICATION) {
      const currentTask = workflow?.findTaskByType(
        OorWorkflowTask.QAR_ORG_VERIFICATION
      )
      const prevTask = workflow?.findTaskByType(
        OorWorkflowTask.PROPOSE_RECIPIENTS
      )
      if (!prevTask?.isTaskCompleted() && !currentTask?.isTaskCompleted()) {
        isDisabled = true
      }
    } else if (taskType == OorWorkflowTask.IDENTITY_VERIFICATION) {
      const currentTask = workflow?.findTaskByType(
        OorWorkflowTask.IDENTITY_VERIFICATION
      )
      const prevTask = workflow?.findTaskByType(
        OorWorkflowTask.QAR_ORG_VERIFICATION
      )
      if (!prevTask?.isTaskCompleted() && !getIsCompletedOrNot(currentTask)) {
        isDisabled = true
      }
    } else if (taskType == OorWorkflowTask.SCHEDULE_ISSUANCE_CEREMONY) {
      const currentTask = workflow?.findTaskByType(
        OorWorkflowTask.SCHEDULE_ISSUANCE_CEREMONY
      )
      const prevTask = workflow?.findTaskByType(
        OorWorkflowTask.IDENTITY_VERIFICATION
      )
      if (!getIsCompletedOrNot(prevTask) && !currentTask?.isTaskCompleted()) {
        isDisabled = true
      }
    } else if (taskType == OorWorkflowTask.ISSUANCE_CEREMONY) {
      const currentTask = workflow?.findTaskByType(
        OorWorkflowTask.ISSUANCE_CEREMONY
      )
      const prevTask = workflow?.findTaskByType(
        OorWorkflowTask.SCHEDULE_ISSUANCE_CEREMONY
      )
      if (!getIsCompletedOrNot(prevTask) && !currentTask?.isTaskCompleted()) {
        isDisabled = true
      }
    } else {
    }
    return isDisabled
  }

  const getIsCompletedOrNot = (task: Task | undefined) => {
    if (!task) return false
    if (task.actors.length === 0) {
      return false
    }
    if (task?.taskType == OorWorkflowTask.IDENTITY_VERIFICATION) {
      if (task.actors.every((actor) => actor.status === TaskStatus.REJECTED))
        return false
      return (
        task.actors.every(
          (actor) =>
            actor.status === TaskStatus.COMPLETE ||
            actor.status === TaskStatus.REJECTED
        ) ||
        (userInfo?.id != workflow?.getRequesterId() &&
          task.actors.some(
            (t) => t.userId === userInfo?.id && t.status === TaskStatus.COMPLETE
          ))
      )
    } else {
      return task?.isTaskCompleted()
    }
  }

  const getTaskStatus = (task: Task | undefined) => {
    if (!task) return TaskStatus.NOT_ASSIGNED
    if (task.actors.length === 0) return TaskStatus.NOT_ASSIGNED
    if (task.taskType === OorWorkflowTask.IDENTITY_VERIFICATION) {
      if (task.actors.every((t) => t.status === TaskStatus.REJECTED))
        return TaskStatus.TERMINATED
      if (
        task.actors.every(
          (t) =>
            t.status === TaskStatus.REJECTED || t.status === TaskStatus.COMPLETE
        ) ||
        (userInfo?.id != workflow?.getRequesterId() &&
          task.actors.some(
            (t) => t.userId === userInfo?.id && t.status === TaskStatus.COMPLETE
          ))
      )
        return TaskStatus.COMPLETE
    }
    return task.calculateTaskStatus()
  }

  const createTaskMenuItem = (
    menuItem: any,
    isSelected: boolean,
    isCompleted: boolean,
    isDisabled: boolean,
    workflowId: string | undefined,
    taskStatus: TaskStatus
  ): TaskMenuItem =>
    ({
      taskType: menuItem.taskType,
      label: menuItem.label,
      href: getHref(menuItem, workflow?.getRequesterId() ?? ''),
      isSelected,
      isCompleted,
      grants: menuItem.grants,
      excludes: menuItem.excludes,
      isDisabled: isDisabled,
      workflowInstanceId: workflowId,
      taskStatus
    }) as TaskMenuItem

  const prepareSideMenuItems = () => {
    const publicItems = oorTaskPageMapping.filter((item) => item.isPublic)
    const tasks = publicItems.map((menuItem) => {
      // `menuItem.taskType` is typed as `string` in the mapping module, but
      // its values are OorWorkflowTask enum keys at runtime. Narrow via the
      // enum's key type so indexing is strict-mode safe.
      const taskTypeKey = menuItem.taskType as keyof typeof OorWorkflowTask
      const task = workflow?.findTaskByType(OorWorkflowTask[taskTypeKey])
      const isSelected = pathname.startsWith(menuItem.href)
      const isCompleted =
        workflow?.getRequesterId() != userInfo?.id &&
        // String enum cross-comparison: both enums share the value string
        // 'IDENTITY_VERIFICATION', but TS rejects direct equality between
        // distinct enum types. Compare as strings.
        (OorWorkflowTask[taskTypeKey] as string) ===
          (TaskType.IDENTITY_VERIFICATION as string)
          ? getTaskStatus(task) === TaskStatus.COMPLETE
          : (task?.isTaskCompleted() ?? false)
      const workflowId = workflow?.getId() ?? undefined
      const isDisabled = getDisabledOrNot(OorWorkflowTask[taskTypeKey])
      const taskStatus = getTaskStatus(task) ?? TaskStatus.NOT_ASSIGNED

      return createTaskMenuItem(
        menuItem,
        isSelected,
        isCompleted,
        isDisabled,
        workflowId,
        taskStatus
      )
    })

    setTaskMenuItems(tasks)
  }

  useEffect(() => {
    prepareSideMenuItems()
    // reason: prepareSideMenuItems closes over many local helpers; useCallback wrapping is risky
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow])

  return (
    <div className="bg-white border rounded shadow-sm border-grey200">
      <p className="p-3 text-base font-semibold">
        <FormattedMessage id="heading.tasksOverView" />
      </p>

      <ul>
        {taskMenuItems.map((menu, index) => (
          <SideMenuItem
            menuItem={menu}
            index={index}
            key={index}
          ></SideMenuItem>
        ))}
      </ul>
    </div>
  )
}
